import { useEffect, useLayoutEffect, useState } from "react";

// Layout effect in the browser (pre-paint cache seed), plain effect during
// SSR — silences React's "useLayoutEffect does nothing on the server"
// warning without changing client behavior.
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
import { useSiteSettings } from "@/context/SiteSettingsContext";
const CACHE_KEY = "pc_site_settings_v1";
const SETTING_KEY = {
  regulations: "regulationsSideNav",
  articles: "articlesSideNav"
};

/**
 * Whether the left section nav should render on an index page.
 *
 * Resolves to FALSE until the real value is known, which is what makes
 * the toggle feel correct: the settings context defaults these to `true`,
 * so a first-time visitor briefly rendered the nav and then had it yanked
 * away once the API answered — a visible flash, plus a layout jump as the
 * 320px gutter collapsed.
 *
 * Returning visitors never see either state change: the settings cache in
 * localStorage is seeded before first paint (useLayoutEffect) and the
 * context reports `loading: false` whenever it has cached data.
 *
 * Navigation uses this too, so the navbar's 320px shift and the nav's own
 * visibility are driven by one answer instead of the navbar guessing from
 * the pathname.
 */
export function useIndexSideNav(entity) {
  const {
    settings,
    loading
  } = useSiteSettings();
  const [cached, setCached] = useState(null);

  // Before paint, so a warm visitor never renders the wrong state.
  useIsomorphicLayoutEffect(() => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const key = SETTING_KEY[entity];
      if (key in parsed) setCached(parsed[key] !== false);
    } catch {
      // no cache / unreadable — stay with the hidden default
    }
  }, [entity]);
  if (!loading) {
    return entity === "regulations" ? settings.regulationsSideNav !== false : settings.articlesSideNav !== false;
  }
  // Unknown: hidden. Never render a nav we might have to take away.
  return cached ?? false;
}